import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auto-attach auth token to every request
// Fuer Aufrufe ausserhalb von apiClient (rohes fetch/axios, FormData).
// Ohne diesen Header antworten die abgesicherten Endpunkte mit 403.
export const authHeaders = () => {
  const t = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 globally - clear token and redirect to login
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('isTrialSession');
      // Only redirect if not already on the login page
      if (!window.location.pathname.includes('reset-password')) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Companies API
export const companiesApi = {
  getAll: () => apiClient.get('/companies/'),
  getById: (id) => apiClient.get(`/companies/${id}`),
  create: (data) => apiClient.post('/companies/', data),
  update: (id, data) => apiClient.put(`/companies/${id}`, data),
  delete: (id) => apiClient.delete(`/companies/${id}`),
  setDefault: (id) => apiClient.post(`/companies/${id}/set-default`),
};

// Customers API
export const customersApi = {
  // 100 war zu wenig: bei 346 Kunden fielen die zuletzt angelegten aus der Liste
  getAll: (companyId, limit = 2000) => apiClient.get('/customers/', { params: { company_id: companyId, limit } }),
  search: (query, companyId, limit = 10) => apiClient.get('/customers/search', { 
    params: { query, company_id: companyId, limit } 
  }),
  getById: (id) => apiClient.get(`/customers/${id}`),
  getRevenues: (customerId) => apiClient.get(`/customers/${customerId}/revenues`),
  create: (data) => apiClient.post('/customers/', data),
  update: (id, data) => apiClient.put(`/customers/${id}`, data),
  delete: (id) => apiClient.delete(`/customers/${id}`),
  // Notes
  addNote: (customerId, noteData) => apiClient.post(`/customers/${customerId}/notes`, noteData),
  getNotes: (customerId) => apiClient.get(`/customers/${customerId}/notes`),
  // Quotes
  getQuotes: (customerId) => apiClient.get(`/customers/${customerId}/quotes`),
  getAcceptedQuotes: (customerId) => apiClient.get(`/customers/${customerId}/accepted-quotes`),
};

// Service Items API
export const serviceItemsApi = {
  getAll: (companyId) => apiClient.get('/service-items/', { params: { companyId: companyId } }),
  getByCategory: (category, companyId) => apiClient.get(`/service-items/by-category/${category}`, { 
    params: { companyId: companyId } 
  }),
  getById: (id) => apiClient.get(`/service-items/${id}`),
  create: (data) => apiClient.post('/service-items/', data),
  update: (id, data) => apiClient.put(`/service-items/${id}`, data),
  delete: (id) => apiClient.delete(`/service-items/${id}`),
};

// Service Categories API
export const serviceCategoriesApi = {
  getAll: (companyId) => apiClient.get('/service-categories/', { params: { company_id: companyId } }),
  create: (companyId, data) => apiClient.post('/service-categories/', data, { params: { company_id: companyId } }),
  update: (categoryId, companyId, data) => apiClient.put(`/service-categories/${categoryId}`, data, { params: { company_id: companyId } }),
  delete: (categoryId, companyId) => apiClient.delete(`/service-categories/${categoryId}`, { params: { company_id: companyId } }),
  initializeDefaults: (companyId) => apiClient.post('/service-categories/initialize-defaults', {}, { params: { company_id: companyId } }),
};

// Service Packages API
export const servicePackagesApi = {
  getAll: (companyId) => apiClient.get('/service-packages/', { params: { company_id: companyId } }),
  getById: (id) => apiClient.get(`/service-packages/${id}`),
  create: (data) => apiClient.post('/service-packages/', data),
  update: (id, data) => apiClient.put(`/service-packages/${id}`, data),
  delete: (id) => apiClient.delete(`/service-packages/${id}`),
  calculateSavings: (id) => apiClient.get(`/service-packages/${id}/calculate-savings`),
};

// Quotes API
export const quotesApi = {
  getAll: (companyId, status) => {
    const params = { company_id: companyId };
    if (status && status !== 'all') params.status = status;
    return apiClient.get('/quotes/', { params });
  },
  getById: (id) => apiClient.get(`/quotes/${id}`),
  get: (id) => apiClient.get(`/quotes/${id}`), // Alias for getById
  create: (data) => apiClient.post('/quotes/', data),
  update: (id, data) => apiClient.put(`/quotes/${id}`, data),
  delete: (id) => apiClient.delete(`/quotes/${id}`),
  duplicate: (id) => apiClient.post(`/quotes/${id}/duplicate`),
  generatePdf: (id) => apiClient.post(`/quotes/${id}/generate-pdf`),
  sendEmail: (id) => apiClient.post(`/quotes/${id}/send-email`),
  getForAcceptance: (id) => apiClient.get(`/quotes/${id}/accept-view`),
  accept: (id, data) => apiClient.post(`/quotes/${id}/accept`, data),
  negotiate: (id, data) => apiClient.post(`/quotes/${id}/negotiate`, data),
  respondToNegotiation: (id, data) => apiClient.post(`/quotes/${id}/respond-negotiation`, data),
  getTracking: (id) => apiClient.get(`/quotes/${id}/tracking`),
  getAllTracking: (companyId) => apiClient.get(`/quotes/tracking/all`, { params: { company_id: companyId } }),
  // Public View & Tracking
  generateViewUrl: (id) => apiClient.post(`/quotes/${id}/generate-view-url`),
  getByViewHash: (viewHash) => apiClient.get(`/quotes/view/${viewHash}`),
  trackView: (viewHash, data) => apiClient.post(`/quotes/view/${viewHash}/track`, data),
  sessionEvent: (viewHash, data) => apiClient.post(`/quotes/view/${viewHash}/session-event`, data),
  getSessions: (quoteId) => apiClient.get(`/quotes/${quoteId}/sessions`),
  trackClick: (viewHash, data) => apiClient.post(`/quotes/view/${viewHash}/track-click`, data),
  // Acceptance Audit PDF
  generateAcceptanceAudit: (id) => apiClient.post(`/quotes/${id}/generate-acceptance-audit`),
  downloadAcceptanceAudit: (id) => `${BACKEND_URL}/api/quotes/${id}/download-acceptance-audit`,
  // Als Blob MIT Auth-Header laden — ein <a href> schickt den JWT nicht mit (403)
  fetchAcceptanceAudit: (id) => apiClient.get(`/quotes/${id}/download-acceptance-audit`, { responseType: 'blob' }),
};

// Quote Intro Generator API
export const quoteIntroApi = {
  generate: (data) => apiClient.post('/quote-intro/generate', data),
};

// Email Templates API
export const emailTemplatesApi = {
  getAll: (companyId) => apiClient.get(`/email-templates/${companyId}`),
  create: (data) => apiClient.post('/email-templates/', data),
  update: (templateId, data) => apiClient.put(`/email-templates/${templateId}`, data),
  delete: (templateId) => apiClient.delete(`/email-templates/${templateId}`),
};

// Revenue Management API
export const revenueApi = {
  getAll: (companyId, filters = {}) => {
    // Clean filters to remove 'all' values and convert field names
    const cleanFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        cleanFilters[key] = value;
      }
    });
    return apiClient.get('/revenue/', { params: { company_id: companyId, ...cleanFilters } });
  },
  getSummary: (companyId) => apiClient.get('/revenue/summary', { params: { company_id: companyId } }),
  create: (data) => apiClient.post('/revenue/', data, { params: { company_id: data.companyId || 'default_company' } }),
  update: (revenueId, data) => apiClient.put(`/revenue/${revenueId}`, data, { params: { company_id: data.companyId || 'default_company' } }),
  delete: (revenueId, companyId) => apiClient.delete(`/revenue/${revenueId}`, { params: { company_id: companyId } }),
  generateRecurring: (revenueId, companyId) => apiClient.post(`/revenue/${revenueId}/generate-recurring`, {}, { params: { company_id: companyId } }),
};

// Expenses API
export const expensesApi = {
  getAll: (companyId, filters = {}) => {
    const cleanFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        cleanFilters[key] = value;
      }
    });
    return apiClient.get('/expenses/', { params: { company_id: companyId, ...cleanFilters } });
  },
  getSummary: (companyId) => apiClient.get('/expenses/summary', { params: { company_id: companyId } }),
  create: (data) => apiClient.post('/expenses/', data, { params: { company_id: data.companyId || 'default_company' } }),
  update: (expenseId, data) => apiClient.put(`/expenses/${expenseId}`, data, { params: { company_id: data.companyId || 'default_company' } }),
  delete: (expenseId, companyId) => apiClient.delete(`/expenses/${expenseId}`, { params: { company_id: companyId } }),
};

export const reconciliationApi = {
  revenues: () => apiClient.get('/reconciliation/revenues'),
};

export const domainsApi = {
  getAll: () => apiClient.get('/domains/'),
  create: (data) => apiClient.post('/domains/', data),
  update: (id, data) => apiClient.put(`/domains/${id}`, data),
  delete: (id) => apiClient.delete(`/domains/${id}`),
  bulkImport: (domains) => apiClient.post('/domains/bulk-import', { domains }),
};

// Cashflow API
export const cashflowApi = {
  getTimeline: (companyId, daysAhead = 90) => apiClient.get('/cashflow/timeline', { params: { company_id: companyId, days_ahead: daysAhead } }),
  getSummary: (companyId, daysAhead = 90) => apiClient.get('/cashflow/summary', { params: { company_id: companyId, days_ahead: daysAhead } }),
};

// Helper function to handle API errors
export const handleApiError = (error) => {
  if (error.response) {
    console.error('API Error:', error.response.data);
    return error.response.data.detail || 'Ein Fehler ist aufgetreten';
  } else if (error.request) {
    console.error('Network Error:', error.request);
    return 'Netzwerkfehler - Bitte prüfen Sie Ihre Verbindung';
  } else {
    console.error('Error:', error.message);
    return error.message;
  }
};
// Documents API
export const documentsApi = {
  getTemplates: () => apiClient.get('/documents/templates'),
  getTemplateDetail: (type) => apiClient.get(`/documents/templates/${type}`),
  saveTemplate: (type, data) => apiClient.put(`/documents/templates/${type}`, data),
  deleteTemplate: (type) => apiClient.delete(`/documents/templates/${type}`),
  getAll: (companyId, customerId) => {
    const params = {}
    if (companyId) params.company_id = companyId
    if (customerId) params.customer_id = customerId
    return apiClient.get('/documents/', { params })
  },
  getById: (id) => apiClient.get(`/documents/${id}`),
  create: (data) => apiClient.post('/documents/', data),
  update: (id, data) => apiClient.put(`/documents/${id}`, data),
  delete: (id) => apiClient.delete(`/documents/${id}`),
  generateLink: (id) => apiClient.post(`/documents/${id}/generate-link`),
  getByViewHash: (hash) => apiClient.get(`/documents/view/${hash}`),
  sign: (hash, data) => apiClient.post(`/documents/view/${hash}/sign`, data),
};
